import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { useGetUserInfoQuery, useUpdateUserInfoMutation } from "store/api";
import useCustomForm from "utils/hooks/useCustomForm";
import useTranslation from "utils/hooks/useTranslation";
import Loader from "components/shared/loader";
import { useEffect, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { addNotification } from "store/componentsSlice";
import { useAppDispatch } from "store/hooks";

export const TarifPopup: React.FC<{
  closePopup: any;
}> = ({
  closePopup,
}) => {
    const { t } = useTranslation();
    const { network } = useSalesNetwork();
    const originalEnergyRef = useRef<EnergyInfo | null>(null);
    const [saveDisabled, setSaveDisabled] = useState(true);
    const [resetDisabled, setResetDisabled] = useState(true);
    const dispatch = useAppDispatch();

    const { data: UserInfoData, refetch, isLoading: isUserInfoLoading } = useGetUserInfoQuery(undefined);
    const [updateUserInfo, { isLoading }] = useUpdateUserInfoMutation();

    const schema: yup.ObjectSchema<EnergyInfo> = yup.object({
      fuelOil: yup.number().required(t("FuelOil is required")),
      electricityFullPrice: yup.number().required(t("ElectricityFullPrice is required")),
      propane: yup.number().required(t("Firstname is required")),
      electricityFullHour: yup.number().required(t("Firstname is required")),
      gas: yup.number().required(t("Firstname is required")),
      electricityFullOffPeack: yup.number().required(t("Firstname is required")),
    });

    const {
      handleSubmit,
      formState: { errors },
      reset,
      control,
      getValues
    } = useCustomForm<EnergyInfo>(
      {
        defaultValues: UserInfoData?.energy,
        resolver: yupResolver(schema),
      }
    );

    useEffect(() => {
      if (UserInfoData?.energy) {
        const original = { ...UserInfoData.energy }; // shallow copy
        originalEnergyRef.current = original;
        reset(original);

        refresh();
      }

    }, [UserInfoData, reset]);

    const watchedEnergy = useWatch({ control });

    useEffect(() => {
      //if (!originalEnergyRef.current) return;

      const changed = JSON.stringify(watchedEnergy) !== JSON.stringify(originalEnergyRef.current || network?._energy); //NOSONAR
      // reset(UserInfoData?.energy);
      setSaveDisabled(!changed);
    }, [watchedEnergy, UserInfoData]);

    useEffect(() => {
      // if (UserInfoData) {
      refresh();
      // }

    }, []);

    useEffect(() => {
      if (!UserInfoData?.energy) {
        reset(network?._energy);
      }
    }, [UserInfoData?.energy, reset]);

    const refresh = async (isReset: boolean = false) => {
      const energy = UserInfoData?.energy && UserInfoData.energy.fuelOil ? UserInfoData.energy : network?._energy
      const userInfos = {
        ...UserInfoData,
        energy,
      };
      // debugger
      const changed = JSON.stringify(network?._energy) !== JSON.stringify(originalEnergyRef.current || energy);

      if (isReset) {
        setSaveDisabled(true);
        setResetDisabled(true);
      } else {
        setSaveDisabled(true);
        setResetDisabled(!changed);
      }

      reset(userInfos.energy);
      originalEnergyRef.current = userInfos.energy ?? null;
    }

    const onReset = async () => {
      const userInfoData = {
        ...UserInfoData,
        energy: null
      };

      try {
        await updateUserInfo(userInfoData).unwrap();

        dispatch(addNotification({
          type: 'success',
          message: t('ENERGY_RESET')
        }));

        reset({});
        originalEnergyRef.current = null;

        setSaveDisabled(true);
        setResetDisabled(true);

        await refetch();
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: t('ERROR_OCCURED')
        }));

        console.error(error);
      } finally {
        if (!UserInfoData?.energy) {
          reset(network?._energy);
        }
      }

    };

    const onSave = async () => {
      const energy = getValues();

      const userInfoData = {
        ...UserInfoData,
        energy: energy
      }
      try {
        await updateUserInfo(userInfoData).unwrap();
        dispatch(addNotification({
          type: 'success',
          message: t('ENERGY_SAVED'),
        }));
        refresh();
        closePopup();
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: t('ERROR_OCCURED'),
        }));
        console.error(error);
      }
    };

    const onCancel = () => {
      refresh();
      closePopup();
    }

    return (<Popup
      title={t("ENERGY_PRICE")}
      translateTitle
      fixSize="medium"
      commandPanelProps={{
        additionalElement: (
          <group data-gap="20">

            {!saveDisabled &&
              // {!hasChanges && 
              <>
                <Button
                  data-length="forcefit"
                  accent
                  secondary
                  large
                  onClick={() => onCancel()}
                >
                  {t("CANCEL")}
                </Button>
                <Button
                  data-length="forcefit"
                  secondary
                  large
                  onClick={handleSubmit(() => onSave())}
                // disabled={!hasChanges}
                >
                  {t("NAV_SAVE")}
                </Button>
              </>
            }
            {false && <Button
              data-length="forcefit"
              secondary
              large
              onClick={handleSubmit(() => onReset())}
            >
              {t("ENERGY_RESET_BUTTON")}
            </Button>}
            {saveDisabled && <Button
              data-length="forcefit"
              secondary
              large
              onClick={() => onCancel()}
            >
              {t("CLOSE")}
            </Button>}
          </group>
        )
      }}
    >
      {isLoading || isUserInfoLoading && <Loader />}
      <group
        data-direction="column"
        data-gap="40"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-direction="column" data-gap="10" data-padding="20">
          <group>
            <text data-weight="400" data-text-size="14" data-ellipsis="" data-color="grey-color-dark" style={{ fontStyle: "italic" }}>
              {t("CURRENCY_TARIFF ({currency})")}
            </text>
          </group>
          {(!saveDisabled || !resetDisabled) && <group data-length="fit" data-border="" data-border-color="main-color" data-radius="4">
            <group data-direction="row" data-align="center" data-justify="space-between" data-gap="10" data-height="50">
              <text data-weight="400" data-text-size="large" data-ellipsis="" data-color="grey-color-dark" data-margin="10">
                {t("ENERGY_DATE_MODIFY")}
              </text>
              <Button large secondary simple onClick={onReset}>{t("ENERGY_RESET_BUTTON")}</Button>
            </group>
          </group>}
        </group>
        <group data-gap="20">
          <group data-gap="20">
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"FUEL_OIL"}
                control={control}
                name="fuelOil"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.fuelOil?.message}
              />
            </group>
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"ELECTRICITY_FULL_PRICE"}
                control={control}
                name="electricityFullPrice"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.electricityFullPrice?.message}
              />
            </group>
          </group>

          <group data-gap="20">
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"FUEL_PROPANE"}
                control={control}
                name="propane"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.propane?.message}
              />
            </group>
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"ELECTRICITY_FULL_HOUR"}
                control={control}
                name="electricityFullHour"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.electricityFullHour?.message}
              />
            </group>
          </group>

          <group data-gap="20">
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"FUEL_GAS"}
                control={control}
                name="gas"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.gas?.message}
              />
            </group>
            <group data-length="forcefit" data-fit="1">
              <Input
                dataLength="autofit"
                label={"ELECTRICITY_FULL_OFF_PEACK"}
                control={control}
                name="electricityFullOffPeack"
                type="number"
                size="large"
                minValue={0}
                step={0.1}
                error={errors.electricityFullOffPeack?.message}
              />
            </group>
          </group>
        </group>
      </group>
    </Popup>
    );
  };
